import { useCallback, useEffect, useState } from 'react';
import { api, call } from '../platform/api.js';
import { leave, navigate } from '../platform/router.js';
import { BackChip } from '../components/back-chip.js';
import { photoQueue } from '../platform/photo-queue.js';
import { Sheet } from '../components/sheet.js';
import { PhotoStrip } from '../components/photo-strip.js';
import { usePhotoUploads } from '../components/use-photo-uploads.js';
import { formatDay } from '@mononoke/shared';
import type { BookingDetail } from '../../../api/src/catalog/bookings.js';
import type { FlagPreset } from '../../../api/src/catalog/flags.js';

/**
 * PER-GARMENT CHECK-IN (frame 4d). The Check-outs board's "Check in…" doors lead here:
 * a booking that is still out, its garments listed, each one tickable, with a note for
 * the whole return and a per-garment "Flag…" for anything that came back wrong.
 *
 * WHY ITS OWN SCREEN, not a bulk button on the board. The board's own comments say it:
 * a one-tap check-in from a card writes movements nobody looked at. The act of checking
 * a garment back in is where damage is noticed, so it gets a screen where you look at
 * each one, not a button that closes the whole booking blind.
 *
 * WHAT IT REUSES. The online-first / queue-on-fallback movement (items.tsx `runMovement`)
 * and the flag raise (item-detail.tsx `FlagsPanel.submit`) are the two established writes;
 * this screen calls the same procedures with the same idempotency discipline. The one new
 * thing on the wire is `bookingId` on check-in (added this release) so the check-in lands
 * in THIS booking's history — without it the checkout shows on the booking and the matching
 * check-in does not.
 *
 * REC (a), maintainer-approved: when the last garment is checked in, the booking flips to
 * `returned` — but only when every tick actually landed on the server. A partial check-in,
 * or one where some ticks queued offline, leaves the booking `out`: it is still out until
 * the queue drains, and saying otherwise would be the reassuring lie G10 forbids.
 */

/** A still-out garment is one the item enum still calls `checked_out`. This mirrors the
 *  SERVER's own definition of what a booking still holds (bookings.ts `setBookingStatus`
 *  returns exactly these) — a garment flagged while out reads `flagged`, not `checked_out`,
 *  the same edge the booking-level return has, so the two never disagree about the set. */
const STILL_OUT = 'checked_out';

/**
 * THE CHECK-IN WALK — "Check in N…" from the Check-outs tab (0.1.527; maintainer, 2 Oct 2026).
 *
 * ⚠️ IT WALKS, IT NEVER CHECKS IN BLIND. The ruling: a bulk check-in walks each ticked reservation's
 * per-garment screen IN TURN — the reason this screen exists (the header above) holds for ten
 * bookings as much as for one. So the bulk act writes nothing; it queues the booking ids and opens
 * the first, and each finished check-in opens the next instead of going back to the list.
 *
 * The queue is a PER-VIEWER CONVENIENCE in sessionStorage — not shared state, and nothing is lost
 * if it is: every check-in still writes its own movements on its own screen. Every access is in
 * try/catch (a private window or blocked storage throws). It is honoured only on the screen it
 * names (`current`), so a walk abandoned through the back chip cannot hijack a later, unrelated
 * check-in; the Check-outs list also forgets it on arrival (`clearCheckinQueue`).
 */
const CHECKIN_QUEUE_KEY = 'mononoke.checkin-queue.v1';

interface CheckinQueue {
  /** The booking whose check-in screen is the walk's current stop. */
  current: string;
  /** The booking ids still to walk, in the order they were ticked on the list. */
  rest: string[];
  total: number;
}

function readCheckinQueue(): CheckinQueue | null {
  try {
    const raw = window.sessionStorage.getItem(CHECKIN_QUEUE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<CheckinQueue> | null;
    if (
      parsed === null ||
      typeof parsed.current !== 'string' ||
      !Array.isArray(parsed.rest) ||
      !parsed.rest.every((x) => typeof x === 'string') ||
      typeof parsed.total !== 'number'
    ) {
      return null;
    }
    return { current: parsed.current, rest: parsed.rest, total: parsed.total };
  } catch {
    return null;
  }
}

function writeCheckinQueue(queue: CheckinQueue): boolean {
  try {
    window.sessionStorage.setItem(CHECKIN_QUEUE_KEY, JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

/** Forget any walk in progress — the list does this on arrival, and a single "Check in…" door
 *  does it before it opens one booking, so a stale walk never chains onto a deliberate one. */
export function clearCheckinQueue(): void {
  try {
    window.sessionStorage.removeItem(CHECKIN_QUEUE_KEY);
  } catch {
    // Nothing to forget if storage is unreachable — and nothing was remembered either.
  }
}

/**
 * Start the walk over `ids` (the list's drawn order) and open the first. Returns false — and opens
 * nothing — when more than one booking was asked for and the browser would not keep the queue:
 * opening the first anyway would quietly drop the rest after it (G10), so the caller says so.
 */
export function startCheckinQueue(ids: readonly string[]): boolean {
  const [first, ...rest] = ids;
  if (first === undefined) return false;
  if (rest.length === 0) clearCheckinQueue();
  else if (!writeCheckinQueue({ current: first, rest, total: ids.length })) return false;
  navigate({ name: 'booking-checkin', id: first });
  return true;
}

/**
 * ⚠️ KEYED BY THE BOOKING (0.1.527): the walk swaps `id` on a mounted screen, and without the key
 * React would keep the last booking's ticks, NOTE and flags on the next one — a condition note
 * written about one return landing in another booking's movements.
 */
export function BookingCheckinScreen({ id }: { id: string }): JSX.Element {
  return <CheckinStop key={id} id={id} />;
}

function CheckinStop({ id }: { id: string }): JSX.Element {
  /** This stop's place in a walk, or null outside one — read once, on arrival. */
  const [queue] = useState<CheckinQueue | null>(() => {
    const q = readCheckinQueue();
    return q !== null && q.current === id ? q : null;
  });
  const position = queue === null ? null : { at: queue.total - queue.rest.length, of: queue.total };
  const [detail, setDetail] = useState<BookingDetail | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Ticked garments. Only still-out ids ever enter it; a re-read that checks one in
   *  clears its tick with it (a tick on a garment no longer out is meaningless). */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** The G10 truth of the last press — "2 checked in ✓ — 1 saved on this device …". */
  const [statusLine, setStatusLine] = useState<string | null>(null);
  /** Signed thumbnail URLs by photo key. A miss or a signing outage is a grey square,
   *  never a broken image — the same quiet degrade the item list takes. */
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  /** Garments flagged in this session, for the row's ⚠ marker. The booking read does not
   *  carry flag state (a garment flagged while still out keeps status `checked_out`), so
   *  the marker is optimistic — the flag itself is durable through the raise/queue. */
  const [flagged, setFlagged] = useState<ReadonlySet<string>>(new Set());
  /** Which garment's Flag… sheet is open. */
  const [flagFor, setFlagFor] = useState<{ id: string; name: string } | null>(null);

  /**
   * Where a FINISHED check-in goes: the walk's next booking when there is one (replacing this
   * entry, so Back from the walk returns to the list, not to a closed booking), else the screen it
   * was opened from as before. A queue the browser will not update ends the walk HERE, said aloud.
   */
  const finish = (): void => {
    const next = queue?.rest[0];
    if (queue !== null && next !== undefined) {
      if (writeCheckinQueue({ current: next, rest: queue.rest.slice(1), total: queue.total })) {
        navigate({ name: 'booking-checkin', id: next }, { replace: true });
        return;
      }
      clearCheckinQueue();
      setProblem('Checked in — but this browser would not keep the rest of the walk. Check in the others from the Check-outs list.');
      return;
    }
    clearCheckinQueue();
    leave({ name: 'checkouts' });
  };
  const fetchThumbs = useCallback(async (keys: (string | null)[]) => {
    const wanted = [...new Set(keys.filter((k): k is string => k !== null))];
    if (wanted.length === 0) return;
    const result = await call('photoUrls', () => api.photoUrls.query({ keys: wanted }));
    if (result.ok) setThumbUrls((known) => ({ ...result.data.urls, ...known }));
  }, []);

  const read = useCallback(async (): Promise<BookingDetail | null> => {
    const result = await call('bookingDetail', () => api.bookingDetail.query({ bookingId: id }));
    if (result.ok) {
      setDetail(result.data);
      void fetchThumbs(result.data.items.map((i) => i.photoKey));
      return result.data;
    }
    setProblem(result.failure.message);
    return null;
  }, [id, fetchThumbs]);

  useEffect(() => {
    void read();
  }, [read]);

  if (problem !== null && detail === null) {
    return (
      <section className="checkin-screen">
        <div className="detail-band">
          <BackChip fallback={{ name: 'checkouts' }} fallbackLabel="← Check-outs" />
        </div>
        <p className="checkin-problem" role="alert">
          {problem}{' '}
          <button type="button" className="linklike" onClick={() => void read()}>
            Retry
          </button>
        </p>
      </section>
    );
  }

  if (detail === null) {
    return (
      <section className="checkin-screen">
        <div className="detail-band">
          <BackChip fallback={{ name: 'checkouts' }} fallbackLabel="← Check-outs" />
        </div>
        <p className="checkin-loading">Loading…</p>
      </section>
    );
  }

  const stillOut = detail.items.filter((i) => i.status === STILL_OUT);
  const tickedCount = stillOut.filter((i) => ticked.has(i.id)).length;

  const toggle = (itemId: string): void => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  /**
   * CHECK THE TICKED GARMENTS BACK IN.
   *
   * ⚠️ SEQUENTIAL, NOT `Promise.all` — the same reason the item list's bulk movement is:
   * each call writes a movement row and re-reads in its own transaction, and firing them
   * all at one connection pool turns a return into a spray of timeouts (items.tsx).
   *
   * ⚠️ ONLINE FIRST, QUEUE ON TRANSPORT FAILURE, SURFACE A REFUSAL. The idempotency key is
   * minted BEFORE the attempt and reused by the queue, so a request that timed out after
   * the server acted meets its own receipt on replay instead of checking the garment in
   * twice (migration 016). Auth failures are NOT queued — the person is standing here.
   */
  const checkIn = async (): Promise<void> => {
    if (busy) return;
    const ids = stillOut.filter((i) => ticked.has(i.id)).map((i) => i.id);
    if (ids.length === 0) return;
    setBusy(true);
    setProblem(null);
    setStatusLine(null);
    const trimmed = note.trim() === '' ? undefined : note.trim();
    let done = 0;
    let queued = 0;
    const refused: string[] = [];
    for (const itemId of ids) {
      const input = { itemId, bookingId: id, ...(trimmed !== undefined ? { note: trimmed } : {}), idempotencyKey: crypto.randomUUID() };
      const result = await call('checkInItem', () => api.checkInItem.mutate(input));
      if (result.ok) done += 1;
      else if (result.failure.kind === 'unavailable') {
        // The one note is applied to every row, the same as the item-list bulk check-in:
        // `movements` is append-only (D4) and this note is often the only record of the
        // condition on return, so dropping it when the act is bulk would degrade the ledger.
        await photoQueue().enqueue(
          { kind: 'movement', payload: { itemId, direction: 'in', bookingId: id, ...(trimmed !== undefined ? { note: trimmed } : {}) } },
          input.idempotencyKey,
        );
        queued += 1;
      } else refused.push(result.failure.message);
    }

    // The rows have moved underneath us — re-read rather than patch, the same rule the
    // booking detail follows after a movement. The fresh read is also what decides the flip.
    const fresh = await read();
    const remaining = (fresh ?? detail).items.filter((i) => i.status === STILL_OUT).length;

    // REC (a): flip only when the booking is genuinely empty AND everything landed online.
    // A queued or refused tick means the server does not yet agree it is all in, so the
    // booking stays `out` and the flip waits for the queue (setBookingStatus is not
    // queueable — it must not be faked offline).
    if (remaining === 0 && queued === 0 && refused.length === 0 && done > 0) {
      const flip = await call('setBookingStatus', () => api.setBookingStatus.mutate({ bookingId: id, status: 'returned' }));
      setBusy(false);
      if (flip.ok) {
        // Everything is in and the booking is closed — back to the screen it was opened from
        // (`leave`, 0.1.524: a finished check-in is not somewhere Back should return to) — or, in a
        // "Check in N…" walk, on to the next booking's check-in (0.1.527, `finish`).
        finish();
        return;
      }
      // The garments are in; only the flip failed. Say so and stay — the booking is not lost,
      // it just still reads `out` and can be closed from its detail screen.
      setProblem(`All garments are checked in, but closing the booking failed: ${flip.failure.message}`);
      return;
    }

    // Partial, queued or refused: stay, keep the remaining rows, and state exactly what
    // happened (G10). Ticks for the garments that went in are gone with the re-read.
    setTicked(new Set());
    // Said only when something did NOT simply land (0.1.523 — success lines went app-wide): the
    // garments that went in leave the list, which is the confirmation; queued and refused are not
    // visible any other way.
    const parts: string[] = [];
    if (queued > 0) parts.push(`${String(queued)} saved on this device — will send when you are back online`);
    if (refused.length > 0) parts.push(`${String(refused.length)} refused: ${refused[0] ?? ''}`);
    setStatusLine(parts.length > 0 ? parts.join(' — ') : null);
    setBusy(false);
  };

  // Open bounds (091) are said, never formatted: `formatDay(null)` throws, and a date nobody chose
  // is the thing 0.1.517 stopped printing.
  const outSince = detail.startsOn === null ? '—' : formatDay(detail.startsOn);
  const dueBack = detail.endsOn === null ? 'no return date' : `due back ${formatDay(detail.endsOn)}`;

  return (
    <section className="checkin-screen">
      <div className="detail-band">
        <BackChip fallback={{ name: 'checkouts' }} fallbackLabel="← Check-outs" />
        <h2 className="detail-header">{detail.title}</h2>
        <span className="screen-head-sub">
          {/* Which stop of a "Check in N…" walk this is (0.1.527) — the walk is otherwise invisible. */}
          {position !== null && <>{`${String(position.at)} of ${String(position.of)}`} · </>}
          {dueBack}
        </span>
      </div>

      <div className="fact-blocks">
        <div className="fact-block">
          <span className="fact-label">Client</span>
          <span className={`fact-value${detail.clientName === null ? ' text-inert' : ''}`}>
            {detail.clientName ?? '—'}
          </span>
        </div>
        <div className="fact-block">
          {/* The booking's start day. It is what "out since" means for a booking that is out —
              the planned start is when the garments left — and it avoids deriving a calendar
              day from a movement instant (the date rule's UTC trap). */}
          <span className="fact-label">Out since</span>
          <span className="fact-value">{outSince}</span>
        </div>
      </div>

      <div className="section-head">
        Still out
        <span className="section-hint">{stillOut.length}</span>
      </div>

      {stillOut.length === 0 ? (
        <>
          <p className="checkin-empty text-inert">Everything on this booking is back.</p>
          {/* A walk's stop that is already all in (checked in elsewhere since it was ticked) has no
              Check in act — without this the walk would strand here (0.1.527). */}
          {queue !== null && queue.rest.length > 0 && (
            <p>
              <button type="button" className="btn-primary chip" onClick={finish}>
                Next booking ({String((position?.at ?? 0) + 1)} of {String(queue.total)})
              </button>
            </p>
          )}
        </>
      ) : (
        <ul className="checkin-list">
          {stillOut.map((item) => {
            const isTicked = ticked.has(item.id);
            const thumb = item.photoKey !== null ? thumbUrls[item.photoKey] : undefined;
            return (
              <li key={item.id} className={`checkin-row${isTicked ? ' checkin-row-ticked' : ''}`}>
                <input
                  type="checkbox"
                  className="select-box"
                  aria-label={`Check in ${item.name}`}
                  checked={isTicked}
                  onChange={() => toggle(item.id)}
                />
                <button type="button" className="checkin-rowface" onClick={() => toggle(item.id)}>
                  {thumb !== undefined ? (
                    <img className="item-thumb" src={thumb} alt="" loading="lazy" />
                  ) : (
                    <span className="item-thumb" aria-hidden="true" />
                  )}
                  <span className="item-main">
                    <strong>{item.name}</strong>
                    <span className="checkin-sub">
                      {item.brand ?? '—'}
                      {flagged.has(item.id) && <span className="checkin-flagged"> · ⚠ Flagged</span>}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="linklike checkin-flag"
                  onClick={() => setFlagFor({ id: item.id, name: item.name })}
                >
                  Flag…
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="section-head">
        A note on the check-in
        <span className="section-hint">optional</span>
      </div>
      <textarea
        className="checkin-note"
        placeholder="Condition on return, anything to pass on"
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
        rows={3}
      />

      {statusLine !== null && (
        <p className="checkin-status" role="status">
          {statusLine}
        </p>
      )}
      {problem !== null && (
        <p className="checkin-problem" role="alert">
          {problem}
        </p>
      )}

      {stillOut.length > 0 && (
        <>
          <div className="float-fade" aria-hidden="true" />
          <div className="float-stack">
            <span className="checkin-tickcount">
              {tickedCount} of {stillOut.length} ticked
            </span>
            <button
              type="button"
              className="float-primary"
              disabled={busy || tickedCount === 0}
              onClick={() => void checkIn()}
            >
              {busy ? 'Checking in…' : `Check in ${tickedCount}`}
            </button>
          </div>
        </>
      )}

      {flagFor !== null && (
        <FlagSheet
          item={flagFor}
          onClose={() => setFlagFor(null)}
          onRaised={(itemId) => {
            setFlagged((prev) => new Set(prev).add(itemId));
            setFlagFor(null);
          }}
        />
      )}
    </section>
  );
}

/**
 * THE PER-GARMENT FLAG SHEET. A compact reprise of `FlagsPanel`'s raise half (item-detail.tsx):
 * the vocabulary as one-tap presets, a free-text reason under them, condition PHOTOS (S18), and
 * the same online-first / queue-on-fallback raise with one idempotency key. It does NOT carry
 * `FlagsPanel`'s preset upkeep, resolve or evidence viewers — those belong to the item's own
 * screen; a garment coming back damaged gets a fast reason AND a photo here, and the full record
 * where it lives.
 */
function FlagSheet({
  item,
  onClose,
  onRaised,
}: {
  item: { id: string; name: string };
  onClose: () => void;
  onRaised: (itemId: string) => void;
}): JSX.Element {
  const [presets, setPresets] = useState<FlagPreset[] | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** Condition photos (S18) — the same capture pipeline `FlagsPanel` uses; `raiseFlag` already
   *  accepts the object keys, so this is a frontend-only reprise. */
  const uploads = usePhotoUploads();
  // Gate the raise on an IN-FLIGHT photo (compressing/queued/uploading), not on `settled`: a
  // FAILED photo rides the queued op instead of blocking the flag (FlagsPanel's contract).
  const photoInFlight = uploads.photos.some(
    (p) => p.status === 'compressing' || p.status === 'queued' || p.status === 'uploading',
  );

  useEffect(() => {
    void (async () => {
      const loaded = await call('listFlagPresets', () => api.listFlagPresets.query());
      if (loaded.ok) setPresets(loaded.data);
      // A failure or a database without the vocabulary leaves it as free-text only — the
      // pre-013 behaviour — rather than blocking the sheet.
      else setPresets([]);
    })();
  }, []);

  const close = (): void => {
    uploads.reset(); // revoke the preview object URLs so a dismissed sheet leaks nothing
    onClose();
  };

  const raise = async (presetId?: string): Promise<void> => {
    if (busy || photoInFlight) return;
    if (presetId === undefined && reason.trim() === '') return;
    setBusy(true);
    setProblem(null);
    const opId = crypto.randomUUID();
    const trimmed = reason.trim() === '' ? undefined : reason.trim();
    // Every uploaded photo's key; the first also fills the legacy single `photoKey` (as FlagsPanel).
    const photoKeys = uploads.photos
      .filter((p) => p.status === 'done')
      .map((p) => p.key)
      .filter((k): k is string => k !== undefined);
    const photoKey = photoKeys[0];
    const result = await call('raiseFlag', () =>
      api.raiseFlag.mutate({ itemId: item.id, reason: trimmed, presetId, photoKey, photoKeys, idempotencyKey: opId }),
    );
    if (result.ok) {
      uploads.reset();
      onRaised(item.id);
    } else if (result.failure.kind === 'unavailable') {
      // Saved on this device, replayed under the same key — a flag raised twice is a second
      // damage report someone has to read and dismiss (item-detail.tsx). Offline the PUT never
      // landed, so the FILE rides the op (one blob → one photo on replay, like FlagsPanel).
      const file = uploads.photos[0]?.file;
      await photoQueue().enqueue(
        { kind: 'flag', payload: { itemId: item.id, reason: trimmed, presetId }, ...(file ? { blob: file } : {}) },
        opId,
      );
      uploads.reset();
      onRaised(item.id);
    } else {
      setProblem(result.failure.message);
      setBusy(false);
    }
  };

  return (
    <Sheet label={`Flag ${item.name}`} onClose={close} className="flag-sheet">
      <h3 className="sheet-title">Flag {item.name}</h3>
      {presets !== null && presets.length > 0 && (
        <div className="flag-presets">
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              className="chip"
              disabled={busy || photoInFlight}
              onClick={() => void raise(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      <textarea
        className="checkin-note"
        placeholder="What is wrong with it?"
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        rows={2}
      />
      {/* Condition photos (S18) — Camera/Gallery + progress thumbs, the same strip FlagsPanel and
          the box/outfit intake use. The raise waits for any in-flight upload to settle. */}
      <PhotoStrip
        photos={uploads.photos}
        onAdd={(files) => void uploads.addFiles(files)}
        onRemove={uploads.removePhoto}
        onRetry={uploads.retryPhoto}
      />
      {problem !== null && (
        <p className="checkin-problem" role="alert">
          {problem}
        </p>
      )}
      <div className="flag-sheet-foot">
        <button type="button" className="chip" onClick={close}>
          Close
        </button>
        <button
          type="button"
          className="float-primary"
          disabled={busy || photoInFlight || reason.trim() === ''}
          onClick={() => void raise()}
        >
          {busy ? 'Flagging…' : photoInFlight ? 'Photo uploading…' : 'Raise flag'}
        </button>
      </div>
    </Sheet>
  );
}
